import { Injectable } from '@tanbo/vue-di-plugin'
import { BehaviorSubject, Observable } from 'rxjs'
import { Http } from '@/services/http'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { useSubscribeDetailsDrawerStore } from '@/stores/subscribe-details-drawer.store'
import { isObjectEmpty } from '@/utils/tool'
import { ElMessageBox } from 'element-plus'
import { CURRENT_PROJECT_SUBJECT, IS_MING_TING_SUBJECT } from '@/project-subject/project-subject'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { AuthStatusList } from '@/views/dashboard/_type/client-item'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import { IS_PRIVATE } from '@/common/const/env'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'
import { i18n } from '@/i18n'

interface PermissionFeMapItem {
  /** vue-i18n key for permissionFe.*.name */
  name: string
  /** vue-i18n key for permissionFe.*.description */
  description?: string
}

/**
 * Frontend permission keys (API / rights map). JSDoc kept in Chinese for internal reference.
 * 用 const 对象代替 enum，便于勒索防护等 key 在日文专版下统一映射（enum 不允许计算值）。
 */
export const PermissionFe = Object.freeze({
  /** 弹窗拦截 */
  popwindow_block: 'popwindow_block',
  /** 正版化软件 */
  soft_authorization: 'soft_authorization',
  /** 远程桌面与协助 & 远程登录认证 */
  remote_control: 'remote_control',
  /** USB设备管控 */
  usb_peripheral_control: 'usb_peripheral_control',
  /** 软件禁用 */
  soft_disable: 'soft_disable',
  /** 文件分发 */
  file_distribute: 'file_distribute',
  /** 网络黑白名单 域名访问限制 */
  internet_url_limit: 'internet_url_limit',
  /** 程序运行控制 */
  proc_control: 'proc_control',
  /** 勒索防护（日文专版统一映射为 jp_scan_ransomware，调用方无需再区分） */
  ransomware_protect: IS_JP_ONLY_BUILD ? 'jp_scan_ransomware' : 'ransomware_protect',
  /** 数据外发管控 */
  dlp_control: 'dlp_control',
  /** 端口管控 */
  port_control: 'port_control',
  /** 终端流量管控 */
  flow_monitor: 'flow_monitor',
  /** 水印管理 */
  watermark_screen: 'watermark_screen',
  /** 安全管理运维组合 linux */
  linux_base_auth: 'linux_base_auth',
  /** IP访问限制 */
  internet_ip_limit: 'internet_ip_limit',
  /** 终端发现 */
  client_find: 'client_find',
  /** 上网限定 */
  internet_speed_limit: 'internet_speed_limit',
  /** 终端远程登录二次认证 */
  remote_login_authentication: 'remote_login_authentication',
  /** 安全事件分析与处置 */
  security_event: 'security_event',
  /** 病毒木马查杀 */
  virus_kill: 'virus_kill',
  /** 漏洞扫描修复 */
  vul_scan_fix: 'vul_scan_fix',
  /** 软件使用统计分析 */
  software_statistics: 'software_statistics',
  /** 基线扫描 */
  baseline_scan: 'baseline_scan',
  /** 软件列表 */
  software_manage: 'software_manage',
  /** 终端安全体检 */
  security_check: 'security_check',
  /** 终端设备及分组管理 */
  device_manage: 'device_manage',
  /** 终端硬件信息 */
  terminal_hardware_info: 'terminal_hardware_info',
  /** 文件白名单 */
  file_whitelist: 'file_whitelist',
  /** 防护策略设置与应用 */
  policy_settings_application: 'policy_settings_application',
  /** 终端在线状态 */
  terminal_online_status: 'terminal_online_status',
  /** 响应处置 */
  response_center: 'response_center',
  /** AI 助手-洞察版（91025） */
  ai_assistant_insight: 'ai_assistant_insight',
  /** AI 助手-研判版（91026） */
  ai_assistant_research: 'ai_assistant_research',
  /** AI 助手-取证版（91027） */
  ai_assistant_forensic: 'ai_assistant_forensic',
})
/** PermissionFe 的 key 联合类型（旧 enum 的 `keyof typeof` 等价物） */
export type PermissionFeKey = keyof typeof PermissionFe

/**
 * Maps permission keys to vue-i18n paths under `permissionFe.<key>.name|description`.
 * In templates use: $t(PermissionFeMap[key].name)
 */
export const PermissionFeMap: { [key: string]: PermissionFeMapItem } = {
  popwindow_block: {
    name: 'permissionFe.popwindow_block.name',
    description: 'permissionFe.popwindow_block.description',
  },
  soft_authorization: {
    name: 'permissionFe.soft_authorization.name',
    description: 'permissionFe.soft_authorization.description',
  },
  remote_control: {
    name: 'permissionFe.remote_control.name',
    description: 'permissionFe.remote_control.description',
  },
  usb_peripheral_control: {
    name: 'permissionFe.usb_peripheral_control.name',
    description: 'permissionFe.usb_peripheral_control.description',
  },
  soft_disable: {
    name: 'permissionFe.soft_disable.name',
    description: 'permissionFe.soft_disable.description',
  },
  file_distribute: {
    name: 'permissionFe.file_distribute.name',
    description: 'permissionFe.file_distribute.description',
  },
  internet_url_limit: {
    name: 'permissionFe.internet_url_limit.name',
    description: 'permissionFe.internet_url_limit.description',
  },
  ransomware_protect: {
    name: 'permissionFe.ransomware_protect.name',
    description: 'permissionFe.ransomware_protect.description',
  },
  // 日文专版的勒索防护 key（PermissionFe.ransomware_protect 在 JP 构建下会映射为此值，展示沿用同一文案）
  jp_scan_ransomware: {
    name: 'permissionFe.ransomware_protect.name',
    description: 'permissionFe.ransomware_protect.description',
  },
  dlp_control: {
    name: 'permissionFe.dlp_control.name',
    description: 'permissionFe.dlp_control.description',
  },
  anti_external_control: {
    name: 'permissionFe.anti_external_control.name',
    description: 'permissionFe.anti_external_control.description',
  },
  proc_control: {
    name: 'permissionFe.proc_control.name',
    description: 'permissionFe.proc_control.description',
  },
  port_control: {
    name: 'permissionFe.port_control.name',
    description: 'permissionFe.port_control.description',
  },
  watermark_screen: {
    name: 'permissionFe.watermark_screen.name',
    description: 'permissionFe.watermark_screen.description',
  },
  internet_ip_limit: {
    name: 'permissionFe.internet_ip_limit.name',
    description: 'permissionFe.internet_ip_limit.description',
  },
  flow_monitor: {
    name: 'permissionFe.flow_monitor.name',
    description: 'permissionFe.flow_monitor.description',
  },
  linux_base_auth: {
    name: 'permissionFe.linux_base_auth.name',
    description: 'permissionFe.linux_base_auth.description',
  },
  client_find: {
    name: 'permissionFe.client_find.name',
    description: 'permissionFe.client_find.description',
  },
  internet_speed_limit: {
    name: 'permissionFe.internet_speed_limit.name',
    description: 'permissionFe.internet_speed_limit.description',
  },
  remote_login_authentication: {
    name: 'permissionFe.remote_login_authentication.name',
    description: 'permissionFe.remote_login_authentication.description',
  },
  security_event: {
    name: 'permissionFe.security_event.name',
    description: 'permissionFe.security_event.description',
  },
  virus_kill: {
    name: 'permissionFe.virus_kill.name',
    description: 'permissionFe.virus_kill.description',
  },
  vul_scan_fix: {
    name: 'permissionFe.vul_scan_fix.name',
    description: 'permissionFe.vul_scan_fix.description',
  },
  software_statistics: {
    name: 'permissionFe.software_statistics.name',
    description: 'permissionFe.software_statistics.description',
  },
  baseline_scan: {
    name: 'permissionFe.baseline_scan.name',
    description: 'permissionFe.baseline_scan.description',
  },
  software_manage: {
    name: 'permissionFe.software_manage.name',
    description: 'permissionFe.software_manage.description',
  },
  security_check: {
    name: 'permissionFe.security_check.name',
    description: 'permissionFe.security_check.description',
  },
  device_manage: {
    name: 'permissionFe.device_manage.name',
    description: 'permissionFe.device_manage.description',
  },
  terminal_hardware_info: {
    name: 'permissionFe.terminal_hardware_info.name',
    description: 'permissionFe.terminal_hardware_info.description',
  },
  file_whitelist: {
    name: 'permissionFe.file_whitelist.name',
    description: 'permissionFe.file_whitelist.description',
  },
  policy_settings_application: {
    name: 'permissionFe.policy_settings_application.name',
    description: 'permissionFe.policy_settings_application.description',
  },
  terminal_online_status: {
    name: 'permissionFe.terminal_online_status.name',
    description: 'permissionFe.terminal_online_status.description',
  },
  response_center: {
    name: 'permissionFe.response_center.name',
    description: 'permissionFe.response_center.description',
  },
}

/** 基础功能（免费赠送）的 permission_name 集合，日本专版下不允许手动配置 */
export const BASIC_SERVICE_PERMISSION_NAMES: ReadonlySet<string> = new Set([
  PermissionFe.virus_kill, // 90001 病毒木马查杀（属基础功能）
  PermissionFe.vul_scan_fix, // 90002 漏洞扫描修复（属基础功能）
  PermissionFe.software_statistics, // 90003 软件使用统计分析（属基础功能）
  PermissionFe.baseline_scan, // 90004 基线扫描（属基础功能）
  PermissionFe.software_manage, // 90005 软件管理（属基础功能）
  PermissionFe.security_check, // 90006 终端安全体检（全面体检）（属基础功能）
  PermissionFe.device_manage, // 90007 终端设备及分组管理（属基础功能）
  PermissionFe.terminal_hardware_info, // 90008 终端硬件信息与预警（属基础功能）
  PermissionFe.file_whitelist, // 91021 文件白名单（属基础功能）
  PermissionFe.policy_settings_application, // 91022 防护策略设置与应用（属基础功能）
  PermissionFe.terminal_online_status, // 91023 终端在线状态（属基础功能）
  PermissionFe.response_center, // 91024 响应处置（属基础功能）
])

export interface OwnPermissionRes {
  permission_list: string[] // 权益名称列表
  permission_map: PermissionMap // 团队权益详情
}

export interface PermissionDetail {
  /** 权益名 */
  permission_name: string
  /** 权益类型,1-按次;2-按时间;3-按次和时间;4-最大限制 */
  service_type: number
  /** 权益可用次数 */
  service_times: number
  /** 权益过期时间 */
  expire_time: number
  /** 最大限制 */
  max_limit: number
  /** 类型 1-试用 2-订阅 */
  type: number
  price_id: number
}

export interface AuthTerminalReq {
  /** 终端ID集合 */
  device_ids: string[]
  is_all_permission?: boolean
  /** 权益服务key */
  permission_names?: string[]
  group_id?: number
  /** true-授权，false-取消授权 */
  is_auth?: boolean
}

export interface ToSubscribeByPriceIdParams {
  normal_price_id?: number
  linux_price_id?: number
  purchase_count?: number
}

export interface PermissionMap { [k: string]: PermissionDetail }

const rightsMapStr = localStorage.getItem('rightsMap') || '{}'
const rightsMap: PermissionMap = JSON.parse(rightsMapStr === 'undefined' ? '{}' : rightsMapStr)
const allRightsMapStr = localStorage.getItem('allRightsMap') || '{}'
const allRightsMap: PermissionMap = JSON.parse(allRightsMapStr === 'undefined' ? '{}' : allRightsMapStr)

@Injectable()
export class ServicePermissionService {
  rightsMapData: PermissionMap = rightsMap
  rightsMap: Observable<PermissionMap>
  allRightsMapData: PermissionMap = allRightsMap
  allRightsMap: Observable<PermissionMap>

  private updateRightsMapEvent = new BehaviorSubject<{[k: string]: PermissionDetail}>(rightsMap)
  private updateAllRightsMapEvent = new BehaviorSubject<{[k: string]: PermissionDetail}>(allRightsMap)

  constructor (private http: Http) {
    this.rightsMap = this.updateRightsMapEvent.asObservable()
    this.allRightsMap = this.updateAllRightsMapEvent.asObservable()
  }

  /** 退出登录时清空团队服务权限缓存（内存 + localStorage） */
  clear () {
    localStorage.removeItem('rightsMap')
    localStorage.removeItem('allRightsMap')
    this.rightsMapData = {}
    this.allRightsMapData = {}
    this.updateRightsMapEvent.next({})
    this.updateAllRightsMapEvent.next({})
  }

  updateRightsMap (c: PermissionMap) {
    localStorage.setItem('rightsMap', JSON.stringify(c))
    this.rightsMapData = c
    this.updateRightsMapEvent.next(c)
  }

  updateAllRightsMap (c: PermissionMap) {
    localStorage.setItem('allRightsMap', JSON.stringify(c))
    this.allRightsMapData = c
    this.updateAllRightsMapEvent.next(c)
  }

  @InvokeThrottle()
  refreshCurrentTeamPermission () {
    return this.http.post<OwnPermissionRes>('/permissiongroup/get_own_permission', {}).then(response => {
      this.updateRightsMap(response.permission_map)
    })
  }

  @InvokeThrottle()
  refreshAllPermission () {
    return this.http.post<{ data: PermissionMap }>('/permissiongroup/all_permission', {}).then(response => {
      this.updateAllRightsMap(response.data)
    })
  }

  getHasPermission (permissionKey) {
    return !isObjectEmpty(this.getPermissionInfo(permissionKey))
  }

  getPermissionInfo (permissionKey) {
    return this.rightsMapData[permissionKey] || {} as PermissionDetail
  }

  getAllPermissionInfo (permissionKey) {
    return this.allRightsMapData[permissionKey] || {} as PermissionDetail
  }

  @InvokeThrottle()
  authTerminal (data: AuthTerminalReq) {
    return this.http.post<void>('/permission_service/terminal_authorize', data)
  }
}

export function toSubscribeByPriceId (params: Partial<ToSubscribeByPriceIdParams>) {
  if (!IS_MING_TING_SUBJECT) {
    agentSubscribeToast()
    return
  }
  if (IS_PRIVATE || IS_JP_ONLY_BUILD) {
    privateSubscribeToast()
    return
  }
  const areaList = []
  params.normal_price_id && areaList.push(AreaType.normal)
  params.linux_price_id && areaList.push(AreaType.linux)
  useSubscribeDetailsDrawerStore().open({
    ...params,
    area_list: areaList.join(','),
  })
}

export function agentSubscribeToast () {
  ElMessageBox.confirm(
    i18n.global.t('permissionFe.toast.agentContact', { phone: CURRENT_PROJECT_SUBJECT.phone_pre_sale }),
    i18n.global.t('permissionFe.dialogTitle'),
    {
      showCancelButton: false,
    }
  )
}

export function privateSubscribeToast () {
  ElMessageBox.confirm(
    i18n.global.t('permissionFe.toast.privateContact'),
    i18n.global.t('permissionFe.dialogTitle'),
    {
      showCancelButton: false,
    }
  )
}

export function getAreaByTerminalType (terminalType: number) {
  if (terminalType === TerminalType.linux) {
    return AreaType.linux
  }
  return AreaType.normal
}

export function getAuthByList (authStatusList: AuthStatusList[], permissionKey: string) {
  const currentItem = authStatusList?.find(v => v.permission_name === permissionKey)
  return currentItem?.auth_status || TerminalAuthStatus.normal
}
